import React, {useContext, useState} from 'react';
import {
    Box, Header, Button, Alert, Link, StatusIndicator, SpaceBetween
} from '@amzn/awsui-components-react';
import {TableProps} from "@amzn/awsui-components-react/polaris/table";
import TPPolarisTable from '../shared/TPPolarisTable';
import {
    MultiAllocationSheetData, WorksheetStatus
} from 'src/models/multidimensional-allocation/MultiAllocationSheetData';
import {
    MultidimensionalAllocationContext
} from 'src/services/multidimensional-allocation/MultidimensionalAllocationContext';
import {
    SHEET_COLUMN_DEFINITIONS,
    SHEET_COLUMN_IDS,
    SHEET_DEFAULT_COLLECTION_PREFERENCES,
    SHEET_FILTERING_PROPERTIES,
    SHEET_VISIBLE_CONTENT_OPTIONS,
    STATUS_INDICATOR_CONFIG,
    PAGE_SIZE_OPTIONS,
} from 'src/components/multidimensional-allocation/common/Constants';
import UploadMDASheetModal from './sheets/UploadMDASheetModal';

/**
 * Component for managing multidimensional allocation sheets
 * Displays a table of sheets with navigation and filtering capabilities
 */
export default function ManageAllocationSheets() {

    const {services} = useContext(MultidimensionalAllocationContext);
    const {multidimensionalAllocationService} = services
    const [sheets, loading, error] = multidimensionalAllocationService.listMultiAllocationSheets('fetch');

    const [createModalVisible, setCreateModalVisible] = useState(false);

    // Create column definitions using centralized constants
    const columnDefinitions: TableProps.ColumnDefinition<MultiAllocationSheetData>[] = [{
        ...SHEET_COLUMN_DEFINITIONS.NAME,
        cell: (item) => (
            <Link href={`/calculations/multidimensionalAllocation/sheet/${item.sheetId}`}>
                {item.sheetName}
            </Link>),
        isRowHeader: true,
    }, {
        ...SHEET_COLUMN_DEFINITIONS.STATUS, cell: (item) => getStatusIndicator(item.status),
    }, {
        ...SHEET_COLUMN_DEFINITIONS.SEGMENT_TYPE, cell: (item) => item.segmentType,
    }, {
        ...SHEET_COLUMN_DEFINITIONS.UPLOADED_AT, cell: (item) => item.lastUpdatedAt,
    }, {
        ...SHEET_COLUMN_DEFINITIONS.UPLOADED_BY, cell: (item) => item.createdBy,
    },];

    // Empty and error state handling
    const tableEmptyState = error ? <Box textAlign="center">
        <Alert type="error" header="Unable to load sheets"
               action={<Button onClick={() => window.location.reload()}>Refresh </Button>}>
            {error}
        </Alert>
    </Box> : <Box textAlign="center">
        <SpaceBetween direction='vertical' size='s'>
            <b>No allocation sheets found</b>
            {"Get started by creating your first multidimensional allocation sheet."}
            <Button variant="primary" onClick={() => setCreateModalVisible(true)}>
                Create multidimensional allocation sheet
            </Button>
        </SpaceBetween>
    </Box>

    const header = <Header actions={ <Button variant='primary' onClick={() => setCreateModalVisible(true)}> 
                                        Create multidimensional allocation sheet 
                                    </Button>
                                } >
                    Multidimensional Allocation Sheets
                </Header>

    return (<>
                <UploadMDASheetModal visible={createModalVisible}
                                     onHide={() => setCreateModalVisible(false)}
                />
        
                <TPPolarisTable
                    header={header}
                    columnDefinitions={columnDefinitions}
                    tableItems={sheets?.sheets || []}
                    isLoading={loading}
                    tableEmptyState={tableEmptyState}
                    resizableColumns={true}
                    sortingColumn="name"
                    filteringProperties={SHEET_FILTERING_PROPERTIES}
                    filteringEmptyText="No sheets match the filter criteria"
                    filteringLoadingText="Loading sheets..."
                    preferencesStorageKey="mda-sheet-table-preferences"
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    visibleContentOptions={SHEET_VISIBLE_CONTENT_OPTIONS}
                    defaultPreferences={SHEET_DEFAULT_COLLECTION_PREFERENCES}
                />
        </>
    );
}


function getStatusIndicator(status: string) {

    const [label, statusType, color] = STATUS_INDICATOR_CONFIG[status as WorksheetStatus];
    return <StatusIndicator type={statusType} colorOverride={color}>
        {label}
    </StatusIndicator>
}
